/**
 * Endpoints that must NOT show the automatic green success toast even though they use a mutating
 * HTTP method (POST/PUT/PATCH/DELETE).
 *
 * By default a successful POST/PUT/PATCH/DELETE shows the generic confirmation toast (see
 * maybeShowSuccessToast in api.tsx), so the user always knows a real data update on an entity
 * (expedition, reservation, location, supplier notification, …) was performed. But this backend
 * also uses POST/PUT for many reads/validations/checks, so the HTTP method alone isn't a reliable
 * "is this a mutation" signal — those endpoints are listed here so they stay silent.
 *
 * Tradeoff of a denylist (vs an allowlist): a NEWLY added read-style POST/PUT will spuriously
 * toast until its name is added here. Chosen deliberately — ~284 of ~334 mutation-method endpoints
 * are real data updates, so the denylist is far smaller and easier to maintain than its inverse.
 *
 * Per-call overrides always win (callbacks.successToast): `false` suppresses a specific call,
 * an object customizes wording, `true` forces a toast even for an endpoint listed here.
 */
export const SUCCESS_TOAST_DENYLIST: ReadonlySet<string> = new Set([
  // Reads / queries / lov / compute / list (POST/PUT used as GET)
  'getReservationStatisticsForDay',
  'lovAllCompaniesByInheritedModules',
  'getReservationsForDayAndMultipleLocations',
  'getFreeSlotsForLocationReservation',
  'cuGetCarrierNote',
  'getDHLAvailableProducts',
  'getFedexAvailableProducts',
  'getFedexPickupAvailability',
  'getMassActionExpeditionData',
  'expeditionList',
  'getConsoleRunners',
  'downloadAttachmentsZip',
  'convertCsvToListObject',
  'computeReservationDurationWithManipulationUnits',
  'aiCarrierAutoSelectionV3',
  'cuOptimizeSupplierNotificationRoutes',

  // Checks / validations (no data is persisted)
  'checkDuplicationExpedition',
  'cuCheckIfGroupAlreadyExists',
  'cuValidateCarriersForExpeditionsV3',
  'checkReservation',
  'checkCompanyDetailDuplicates',
  'checkImportEnnoviExpedition',
  'checkCompanyIfExists',
  'checkReservationIfExpeditionCanBeCreated',

  'uploadAttachments',

  // AI dispatcher rule authoring — read-style POSTs. validate runs on every keystroke and format
  // on every builder edit, so a toast per call would be relentless; dry-run evaluates a rule
  // against a real expedition and explicitly performs no action. Saving and deleting a rule are
  // real data updates and keep the standard toast.
  'validateRule',
  'formatRule',
  'dryRunRule',
  'adminValidateRule',
  'adminFormatRule',
  'adminDryRunRule',

  // AI dispatcher robot suggestions on the expedition. `accept` is bookkeeping that follows a real
  // award or distribution — the endpoint that performed it already toasted — and `dismiss` shows
  // its own result by making the card disappear.
  'acceptRobotSuggestion',
  'dismissRobotSuggestion',

  // Previews / downloads / exports / PDF & report generation
  'adminWebhookPreview',
  'generateDachserExportAndDownload',
  'downloadPODFromDachser',
  'startExportJob',
  'createExpeditionDetailPdf',
  'createOrderConfirmationPdf',

  // Webhook receiver / console / admin maintenance / token fetch / test
  'handleWebhook',
  'runConsoleRunner',
  'adminRebuildDashboard',
  'sendPowerBIToken',
  'generateAndAttachNewIntegrationToken',
  'submitInvoiceValidationResponse',

  // Gate / camera commands (not entity data)
  'pocOpenGate',
  'adminCameraOpen',

  // AI messenger (generates suggested text, not a save)
  'requestTimeslotGoToRampMessage',
  'requestTimeslotOnTimeCheckMessage',

  // Pure notify / messaging / login (own feedback, or too noisy)
  'sendExpeditionTimeslotReminder',
  'reservationNotifyDriver',
  'caSendMessageV2',
  'cuSendMessageV2',
  'cuSendMultipleMessagesV2',
  'sendLoginToMobileApp',

  // Mark-as-read / favorite toggles (own immediate UI feedback, noisy)
  'enableRule',
  'disableRule',
  'adminEnableRule',
  'adminDisableRule',
  'readCustomerNotification',
  'cuMarkAllNotificationAsRead',
  'favoriteExpedition',
  'cuReadNotification',

  // Per-user filter preference saves (frequent, not entity data)
  'saveUserExpeditionFilters',
  'saveUserReservationFilters',
  'saveUserSupplierNotificationFilters',
  'saveUserVisitorRecordFilters',
  'saveUserTableFilterFavourites',
  'deleteUserTableFilterFavourites',
  // Table tabs (RD-2957): order, hidden tabs and the open tab — saved on every tab click.
  'saveTableViewPrefs',

  // Real data-updates that ALREADY show their own specific success toast at every call site —
  // denylisted so the generic toast doesn't double up. (If a new call site of one of these needs
  // confirmation and shows no toast of its own, pass successToast:true on that call.)
  'createExpedition',
  'createSupplierNotification',
  'upsertTimeslotModuleSettings',
  'upsertSnModuleSettings',
  'adminUpdateCompanyDetail',
  'adminDeleteCompany',
  'deleteCompanyFolder',
  'deleteCompanyFolderCustomer',
  'adminCreateUserInAuth0',
  'adminMoveLogsteoUserToCompanyUser',
]);